export const HISTORICAL_PRODUCT_SEARCH_TOOL_NAME = "search_historical_products";
export const HISTORICAL_PRODUCT_SEARCH_TIMEOUT_MS = 20_000;

const BUSINESS_STATUS_BY_CODE = {
  VALIDATION_ERROR: 400,
  NOT_FOUND: 404,
  RATE_LIMIT: 429,
};

const VALID_DATA_KEYS = new Set([
  "product_id",
  "name",
  "sku",
  "item_number",
  "availability",
  "categories",
  "attributes",
  "image",
]);

function validationError() {
  return {
    success: false,
    error: {
      code: "VALIDATION_ERROR",
      message: "A non-empty historical product query is required.",
    },
  };
}

function hasValidSearchInput(input) {
  return !!input
    && typeof input === "object"
    && !Array.isArray(input)
    && Object.keys(input).length === 1
    && Object.hasOwn(input, "query")
    && typeof input.query === "string"
    && input.query.trim().length > 0;
}

function isStringMap(value) {
  return !!value
    && typeof value === "object"
    && !Array.isArray(value)
    && Object.values(value).every(item => typeof item === "string" && item.length > 0);
}

function isValidProductData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  if (Object.keys(data).some(key => !VALID_DATA_KEYS.has(key))) return false;
  if (!Number.isInteger(data.product_id) || data.product_id <= 0) return false;
  if (typeof data.name !== "string" || !data.name) return false;
  if (data.availability !== "unavailable") return false;
  if (!Array.isArray(data.categories) || !data.categories.every(item => typeof item === "string" && item.length > 0)) return false;
  if (!isStringMap(data.attributes)) return false;

  for (const key of ["sku", "item_number", "image"]) {
    if (Object.hasOwn(data, key) && (typeof data[key] !== "string" || !data[key])) return false;
  }

  return true;
}

function isValidSuccessData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  if (Object.keys(data).length !== 2) return false;
  if (typeof data.query !== "string" || !data.query) return false;
  if (!Array.isArray(data.results) || data.results.length < 1 || data.results.length > 5) return false;
  return data.results.every(isValidProductData);
}

function isValidBusinessResponse(output, status) {
  if (!output || typeof output !== "object" || Array.isArray(output)) return false;

  if (output.success === true) {
    return status === 200
      && Object.keys(output).length === 2
      && Object.hasOwn(output, "data")
      && isValidSuccessData(output.data);
  }

  if (output.success !== false || Object.keys(output).length !== 2 || !output.error) return false;
  const { code, message } = output.error;

  return Object.keys(output.error).length === 2
    && typeof message === "string"
    && message.length > 0
    && BUSINESS_STATUS_BY_CODE[code] === status;
}

function outputError(addToolOutput, toolCall, errorText) {
  return addToolOutput({
    tool: toolCall.toolName,
    toolCallId: toolCall.toolCallId,
    state: "output-error",
    errorText,
  });
}

export async function handleHistoricalProductSearchToolCall({
  toolCall,
  endpointUrl,
  addToolOutput,
  fetchImpl = globalThis.fetch,
  AbortControllerImpl = globalThis.AbortController,
  setTimeoutImpl = globalThis.setTimeout,
  clearTimeoutImpl = globalThis.clearTimeout,
}) {
  if (toolCall?.toolName !== HISTORICAL_PRODUCT_SEARCH_TOOL_NAME) return false;

  if (!hasValidSearchInput(toolCall.input)) {
    await addToolOutput({
      tool: toolCall.toolName,
      toolCallId: toolCall.toolCallId,
      output: validationError(),
    });
    return true;
  }

  if (typeof endpointUrl !== "string" || !endpointUrl.trim()) {
    await outputError(addToolOutput, toolCall, "The historical product search is unavailable.");
    return true;
  }

  const controller = new AbortControllerImpl();
  const timeoutId = setTimeoutImpl(() => controller.abort(), HISTORICAL_PRODUCT_SEARCH_TIMEOUT_MS);

  try {
    const response = await fetchImpl(endpointUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query: toolCall.input.query.trim() }),
      signal: controller.signal,
    });
    const output = await response.json();

    if (!isValidBusinessResponse(output, response.status)) {
      throw new Error("Invalid historical product search response.");
    }

    await addToolOutput({
      tool: toolCall.toolName,
      toolCallId: toolCall.toolCallId,
      output,
    });
  } catch (error) {
    const timedOut = controller.signal.aborted || error?.name === "AbortError";
    await outputError(
      addToolOutput,
      toolCall,
      timedOut ? "The historical product search timed out." : "The historical product search could not be completed.",
    );
  } finally {
    clearTimeoutImpl(timeoutId);
  }

  return true;
}
